import React, { useEffect } from 'react';
import ServicePageTemplate from '../components/ServicePageTemplate';

import {
  DollarSign,
  MapPin,
  Send,
  FileText,
  BarChart3,
  Briefcase,
  TrendingUp,
  Search,
  Handshake,
  Target,
  Building2,
  Users,
  Crown,
  Building,
  CheckCircle2
} from 'lucide-react';


// ============================================================
// REUSABLE SEO + CONTENT COMPONENTS
// ============================================================

const usePageSEO = (title, description) => {
  useEffect(() => {
    document.title = title;

    let meta = document.querySelector('meta[name="description"]');

    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }

    meta.setAttribute('content', description);
  }, [title, description]);
};


const ContentSection = ({
  title,
  description,
  items = [],
  gradient = 'from-emerald-400 to-green-500'
}) => {
  return (
    <section className="bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950 text-white py-20 px-6">
      <div className="max-w-6xl mx-auto">

        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {title}
          </h2>

          {description && (
            <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
              {description}
            </p>
          )}
        </div>

        {items.length > 0 && (
          <div className="grid md:grid-cols-2 gap-8">
            {items.map((item, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-2xl p-8"
              >
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} bg-opacity-20 flex items-center justify-center mb-6`}
                >
                  <span className="text-emerald-300 text-xl font-bold">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>

                <h3 className="text-2xl font-bold mb-4">
                  {item.title}
                </h3>

                <p className="text-gray-300 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        )}

      </div>
    </section>
  );
};


const ProcessSection = ({ title, description, steps }) => {
  return (
    <section className="bg-gray-950 text-white py-20 px-6">
      <div className="max-w-6xl mx-auto">

        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-5">
            {title}
          </h2>

          {description && (
            <p className="text-gray-300 max-w-3xl mx-auto text-lg">
              {description}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className="bg-white/5 border border-white/10 rounded-xl p-6 text-center"
            >
              <div className="text-emerald-300 text-3xl font-bold mb-4">
                {String(index + 1).padStart(2, '0')}
              </div>

              <h3 className="text-xl font-bold mb-3">
                {step.title}
              </h3>

              <p className="text-gray-300 text-sm leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};


const FAQSection = ({ faqs }) => {
  return (
    <section className="bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950 text-white py-20 px-6">
      <div className="max-w-4xl mx-auto">

        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold mb-4">
            Frequently Asked Questions
          </h2>

          <p className="text-gray-300">
            Common questions about this service and how it can support your
            fundraising or investment objectives.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <details
              key={index}
              className="bg-white/5 border border-white/10 rounded-xl p-6"
            >
              <summary className="cursor-pointer text-lg font-semibold text-white">
                {faq.question}
              </summary>

              <p className="text-gray-300 leading-relaxed mt-4">
                {faq.answer}
              </p>
            </details>
          ))}
        </div>

      </div>
    </section>
  );
};


const RelatedServices = ({ links }) => {
  return (
    <section className="bg-gray-950 text-white py-16 px-6">
      <div className="max-w-6xl mx-auto">

        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">
            Explore Related Services
          </h2>

          <p className="text-gray-300">
            Explore other services that can support different parts of your
            fundraising or investment process.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {links.map((link, index) => (
            <a
              key={index}
              href={link.href}
              className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all"
            >
              <span className="text-white font-medium">
                {link.label}
              </span>
            </a>
          ))}
        </div>

      </div>
    </section>
  );
};


const FinalCTA = ({
  title = 'Ready to Get Started?',
  description = 'Tell us about your startup, investment requirement, or business objective and we can discuss the next steps.'
}) => {
  return (
    <section className="bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950 text-white py-20 px-6">
      <div className="max-w-4xl mx-auto text-center">

        <h2 className="text-4xl font-bold mb-5">
          {title}
        </h2>

        <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-8">
          {description}
        </p>

        <a
          href="/contact"
          className="inline-flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
        >
          Contact FounderScale Capital
        </a>

      </div>
    </section>
  );
};


// ============================================================
// STARTUP SERVICES
// ============================================================


export const FundraisingAssistance = () => {

  usePageSEO(
    'Startup Fundraising Assistance | FounderScale Capital',
    'Get end-to-end startup fundraising assistance from FounderScale Capital, including fundraising strategy, investor targeting, outreach, meeting preparation, due diligence, and closing support.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={DollarSign}
        title="Startup Fundraising Assistance"
        subtitle="End-to-end support to help you raise capital — from preparation to closing"
        description="FounderScale Capital provides structured fundraising assistance for startups across different stages. We support founders with fundraising strategy, investor preparation, investor discovery, outreach, meeting preparation, due diligence, and closing support."
        features={[
          'Comprehensive fundraising strategy',
          'Investor readiness assessment',
          'Investor list curation',
          'Pitch deck refinement',
          'Financial model preparation',
          'Fundraising narrative development',
          'Email and LinkedIn outreach',
          'Investor follow-up management',
          'Meeting preparation and coaching',
          'Due diligence support',
          'Investor communication support',
          'Term sheet negotiation guidance',
          'Closing assistance'
        ]}
        benefits={[
          {
            title: 'Save Time',
            description:
              'Focus on building your company while your fundraising process is organized around clear priorities.'
          },
          {
            title: 'Better Investor Targeting',
            description:
              'Build an investor pipeline around your startup stage, sector, geography and fundraising requirements.'
          },
          {
            title: 'Structured Process',
            description:
              'Move from preparation to investor outreach, meetings, diligence and closing through a defined process.'
          }
        ]}
        gradient="from-amber-400 to-orange-500"
      />

      <ContentSection
        title="End-to-End Fundraising Support"
        description="Fundraising involves much more than sending a pitch deck. Founders need a clear fundraising strategy, appropriate investor targeting, strong documentation and consistent investor communication."
        items={[
          {
            title: 'Fundraising Strategy & Preparation',
            description:
              'Review the startup, fundraising requirement, business model, traction and objectives to structure an appropriate fundraising approach.'
          },
          {
            title: 'Investor Discovery & Targeting',
            description:
              'Research potential investors based on stage, sector, geography, investment thesis, portfolio focus and potential cheque size.'
          },
          {
            title: 'Investor Outreach & Follow-Up',
            description:
              'Support structured email, LinkedIn and introduction-based outreach while organizing responses, follow-ups and investor conversations.'
          },
          {
            title: 'Meetings, Diligence & Closing',
            description:
              'Support founders as investor conversations progress into meetings, information requests, due diligence, negotiation and closing.'
          }
        ]}
      />

      <ProcessSection
        title="Our Fundraising Assistance Process"
        description="A structured process helps founders move from preparation to investor conversations with greater clarity."
        steps={[
          {
            title: 'Discovery',
            description:
              'Understand the startup, current stage, business model, traction and capital requirement.'
          },
          {
            title: 'Preparation',
            description:
              'Organize fundraising materials, narrative, financial information and investor documentation.'
          },
          {
            title: 'Investor Outreach',
            description:
              'Identify relevant investors and execute structured outreach and follow-up.'
          },
          {
            title: 'Execution',
            description:
              'Support meetings, diligence, investor communication and later-stage fundraising activities.'
          }
        ]}
      />

      <RelatedServices
        links={[
          {
            label: 'Investor Discovery',
            href: '/services/investor-discovery'
          },
          {
            label: 'Investor Outreach',
            href: '/services/investor-outreach'
          },
          {
            label: 'Pitch Deck Creation',
            href: '/services/pitch-deck'
          },
          {
            label: 'Financial Modeling',
            href: '/services/financial-modeling'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What does fundraising assistance include?',
            answer:
              'Fundraising assistance can include fundraising strategy, investor preparation, investor discovery, investor outreach, meeting preparation, follow-up management, due diligence support, negotiation guidance and closing support.'
          },
          {
            question: 'Do you help startups find investors?',
            answer:
              'Yes. Investor discovery and mapping can be part of the fundraising process, with potential investors researched according to stage, sector, geography, investment focus and other relevant criteria.'
          },
          {
            question: 'Do you help with investor outreach?',
            answer:
              'Yes. Support can include personalized email outreach, LinkedIn communication, follow-ups, investor pipeline organization and meeting preparation.'
          },
          {
            question: 'Can you help prepare fundraising documents?',
            answer:
              'Fundraising support can include pitch deck refinement, financial model preparation, data room preparation and other investor-facing documentation depending on the startup’s requirements.'
          },
          {
            question: 'Which startup stages do you support?',
            answer:
              'FounderScale Capital supports startups across ideation, early-stage, growth-stage and pre-Series A fundraising requirements, depending on the company and its objectives.'
          },
          {
            question: 'How does the process begin?',
            answer:
              'The process begins with understanding the startup, its current stage, fundraising requirement, business model, traction and objectives before structuring the appropriate fundraising process.'
          }
        ]}
      />

      <FinalCTA
        title="Ready to Structure Your Fundraising Process?"
        description="Tell us about your startup, current stage and fundraising requirement to discuss the type of fundraising support that may be relevant."
      />

    </div>
  );
};


// ============================================================
// INVESTOR DISCOVERY
// ============================================================


export const InvestorDiscovery = () => {

  usePageSEO(
    'Investor Discovery & Mapping | Find Startup Investors | FounderScale Capital',
    'Find relevant startup investors with FounderScale Capital. Our investor discovery and mapping process considers startup stage, sector, geography, investment thesis, portfolio focus and cheque size.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={MapPin}
        title="Investor Discovery & Mapping"
        subtitle="Find relevant investors with a structured, research-driven approach"
        description="FounderScale Capital helps startups identify and organize relevant investors based on stage, sector, geography, investment thesis, portfolio focus and potential cheque size."
        features={[
          'Curated investor research',
          'Investor segmentation by stage',
          'Sector and industry analysis',
          'Geographic preference research',
          'Investment thesis matching',
          'Portfolio analysis',
          'Recent investment activity',
          'Investor preference research',
          'Contact information research',
          'Investor pipeline organization'
        ]}
        gradient="from-emerald-400 to-green-600"
      />

      <ContentSection
        title="Build a More Relevant Investor Pipeline"
        description="Investor discovery is about understanding which investors may be relevant before beginning outreach."
        items={[
          {
            title: 'Investment Stage',
            description:
              'Research whether investors focus on ideation, pre-seed, seed, Series A, growth-stage or later opportunities.'
          },
          {
            title: 'Sector & Industry Focus',
            description:
              'Review investor interests across sectors such as SaaS, fintech, healthcare, AI, consumer businesses, e-commerce and other relevant industries.'
          },
          {
            title: 'Geography & Market Preference',
            description:
              'Organize investor research according to country, region and market preferences where relevant to the fundraising strategy.'
          },
          {
            title: 'Investment Thesis & Portfolio',
            description:
              'Review publicly available investment theses, portfolio companies and relevant investment activity to understand potential alignment.'
          }
        ]}
      />

      <ProcessSection
        title="Our Investor Mapping Process"
        description="Investor research can be organized into four practical stages."
        steps={[
          {
            title: 'Understand',
            description:
              'Understand the startup, stage, sector, geography, traction and fundraising requirement.'
          },
          {
            title: 'Research',
            description:
              'Research investors, their investment focus, portfolio, stage preferences and relevant activity.'
          },
          {
            title: 'Segment',
            description:
              'Organize potential investors according to relevance and fundraising requirements.'
          },
          {
            title: 'Prepare',
            description:
              'Organize investor information and contact details for structured outreach.'
          }
        ]}
      />

      <RelatedServices
        links={[
          {
            label: 'Fundraising Assistance',
            href: '/services/fundraising-assistance'
          },
          {
            label: 'Investor Outreach',
            href: '/services/investor-outreach'
          },
          {
            label: 'Pitch Deck Creation',
            href: '/services/pitch-deck'
          },
          {
            label: 'Data Room Creation',
            href: '/services/data-room'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is investor discovery and mapping?',
            answer:
              'It is the process of identifying and organizing potential investors according to factors such as startup stage, sector, geography, investment thesis, portfolio focus and potential cheque size.'
          },
          {
            question: 'How do you identify relevant investors?',
            answer:
              'Investor research can consider publicly available information including investment focus, portfolio companies, stage preferences, sector interests, geography and investment activity.'
          },
          {
            question: 'Can investors be filtered by startup stage?',
            answer:
              'Yes. Investor research can be organized around stages such as pre-seed, seed, Series A, growth-stage and later-stage investing.'
          },
          {
            question: 'Can investors be filtered by sector?',
            answer:
              'Yes. Sector focus can be one of the criteria used to organize investor research.'
          },
          {
            question: 'Does investor mapping include contact information?',
            answer:
              'Depending on available information, investor research can include contact details, professional profiles, firm information and investment background.'
          },
          {
            question: 'What happens after investor discovery?',
            answer:
              'Once relevant investors are identified, founders can move into a structured outreach process involving personalized communication, follow-ups and meetings.'
          }
        ]}
      />

      <FinalCTA
        title="Looking for Relevant Investors?"
        description="Tell us about your startup, fundraising stage, sector, geography and capital requirement to discuss investor discovery and mapping."
      />

    </div>
  );
};


// ============================================================
// INVESTOR OUTREACH
// ============================================================


export const InvestorOutreach = () => {

  usePageSEO(
    'Investor Outreach Services for Startups | FounderScale Capital',
    'FounderScale Capital provides structured investor outreach for startups through personalized email, LinkedIn communication, follow-ups, investor pipeline management and meeting support.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Send}
        title="Investor Outreach Services"
        subtitle="Structured, personalized outreach to put your startup in front of relevant investors"
        description="FounderScale Capital helps startups execute structured investor outreach campaigns across email, LinkedIn and available introduction channels. We support founders with messaging, outreach sequencing, follow-ups, meeting coordination and investor pipeline management."
        features={[
          'Personalized investor email campaigns',
          'LinkedIn messaging and engagement',
          'Warm introduction paths where available',
          'Investor-specific messaging',
          'Outreach sequencing',
          'Follow-up management',
          'Meeting scheduling support',
          'Response tracking',
          'Investor pipeline management',
          'Campaign reporting'
        ]}
        gradient="from-blue-400 to-cyan-500"
      />

      <ContentSection
        title="Structured Investor Outreach for Startups"
        description="Investor outreach works best when the right investors receive relevant information through a clear and organized process."
        items={[
          {
            title: 'Investor-Specific Messaging',
            description:
              'Structure communication around the investor’s publicly available focus, sector interests, stage preference and the startup’s fundraising story.'
          },
          {
            title: 'Email Outreach',
            description:
              'Organize professional email campaigns using structured messaging, investor segmentation, sequencing and follow-up processes.'
          },
          {
            title: 'LinkedIn Outreach',
            description:
              'Use professional LinkedIn communication for introductions, relationship building, relevant messages and follow-up.'
          },
          {
            title: 'Follow-Up Management',
            description:
              'Track investor responses, next steps and ongoing conversations so that opportunities are not lost in an unstructured process.'
          }
        ]}
      />

      <ProcessSection
        title="Our Investor Outreach Process"
        description="A structured outreach process moves from investor targeting to communication and pipeline management."
        steps={[
          {
            title: 'Target',
            description:
              'Start with relevant investors identified through investor discovery.'
          },
          {
            title: 'Prepare',
            description:
              'Prepare investor-facing messaging and relevant fundraising information.'
          },
          {
            title: 'Reach Out',
            description:
              'Execute personalized communication through relevant professional channels.'
          },
          {
            title: 'Follow Up',
            description:
              'Track responses and maintain organized follow-up activities.'
          }
        ]}
      />

      <section className="bg-gray-950 text-white py-20 px-6">
        <div className="max-w-6xl mx-auto">

          <div className="grid md:grid-cols-3 gap-6">

            <div className="bg-white/5 border border-white/10 rounded-xl p-7">
              <h3 className="text-2xl font-bold mb-4">
                Email Outreach
              </h3>

              <p className="text-gray-300 leading-relaxed">
                Structured email communication can introduce the startup,
                explain the fundraising requirement and provide relevant
                information for an investor to consider.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-7">
              <h3 className="text-2xl font-bold mb-4">
                LinkedIn Outreach
              </h3>

              <p className="text-gray-300 leading-relaxed">
                LinkedIn can support professional introductions, relationship
                building, investor communication and follow-up.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-7">
              <h3 className="text-2xl font-bold mb-4">
                Warm Introductions
              </h3>

              <p className="text-gray-300 leading-relaxed">
                Where an appropriate connection is available, a warm
                introduction can provide another route for beginning an
                investor conversation.
              </p>
            </div>

          </div>

        </div>
      </section>

      <RelatedServices
        links={[
          {
            label: 'Fundraising Assistance',
            href: '/services/fundraising-assistance'
          },
          {
            label: 'Investor Discovery',
            href: '/services/investor-discovery'
          },
          {
            label: 'Pitch Deck Creation',
            href: '/services/pitch-deck'
          },
          {
            label: 'Data Room Creation',
            href: '/services/data-room'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is investor outreach?',
            answer:
              'Investor outreach is the process of communicating with relevant investors about a startup fundraising opportunity through email, LinkedIn, introductions and follow-up.'
          },
          {
            question: 'Do you personalize investor outreach?',
            answer:
              'Outreach can be structured around publicly available investor information, including sector interests, stage preference, portfolio and investment focus.'
          },
          {
            question: 'Which channels do you use?',
            answer:
              'Outreach can include professional email communication, LinkedIn messaging and engagement, and warm introductions where an appropriate connection is available.'
          },
          {
            question: 'Do you manage follow-ups?',
            answer:
              'Yes. Follow-up management can include tracking responses, organizing next steps and maintaining communication records.'
          },
          {
            question: 'How do you track outreach?',
            answer:
              'Investor pipelines can track outreach status, responses, meetings, follow-ups, investor interest and outstanding next steps.'
          },
          {
            question: 'What happens after an investor responds?',
            answer:
              'The next step can include scheduling a meeting, preparing relevant information, answering investor questions and managing subsequent conversations.'
          }
        ]}
      />

      <FinalCTA
        title="Ready to Start Investor Outreach?"
        description="Tell us about your startup, fundraising stage, target amount and investor requirements to discuss a structured outreach process."
      />

    </div>
  );
};


// ============================================================
// DATA ROOM
// ============================================================


export const DataRoom = () => {

  usePageSEO(
    'Data Room Creation for Startups | Investor Due Diligence | FounderScale Capital',
    'Build an investor-ready startup data room with FounderScale Capital. Organize financial, legal, commercial, team, product and fundraising documents for investor due diligence.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={FileText}
        title="Data Room Creation"
        subtitle="Investor-ready data rooms that streamline due diligence"
        description="A well-organized data room helps founders present important company information in a structured and accessible way. FounderScale Capital helps organize financial, legal, commercial, team, product and fundraising documents for investor review."
        features={[
          'Comprehensive data room structure',
          'Financial document organization',
          'Legal document organization',
          'Cap table and equity documentation',
          'Customer contracts and case studies',
          'Team profiles and organizational information',
          'Product roadmap and technology information',
          'Market research and competitive analysis',
          'Fundraising documentation',
          'Due diligence checklist'
        ]}
        gradient="from-rose-400 to-red-500"
      />

      <ContentSection
        title="Build an Investor-Ready Data Room"
        description="Investors may request information across several areas of a business during due diligence. A structured data room helps founders organize these materials before or during the fundraising process."
        items={[
          {
            title: 'Financial Information',
            description:
              'Organize financial statements, forecasts, revenue information, operating expenses, unit economics and other relevant financial materials.'
          },
          {
            title: 'Legal & Corporate Documents',
            description:
              'Organize incorporation documents, ownership information, agreements, contracts and other relevant corporate documentation.'
          },
          {
            title: 'Commercial Information',
            description:
              'Present customer information, contracts, partnerships, case studies, sales information and other relevant commercial materials.'
          },
          {
            title: 'Product & Technology',
            description:
              'Organize product information, technology documentation, roadmap materials and relevant intellectual property information.'
          }
        ]}
      />

      <ProcessSection
        title="Data Room Preparation Process"
        description="A structured preparation process helps founders identify and organize the information investors may request."
        steps={[
          {
            title: 'Audit',
            description:
              'Review the existing documents and identify important information that needs to be organized.'
          },
          {
            title: 'Structure',
            description:
              'Create a logical folder structure covering financial, legal, commercial, product and team information.'
          },
          {
            title: 'Organize',
            description:
              'Place relevant documents in the appropriate sections and identify missing or outdated materials.'
          },
          {
            title: 'Prepare',
            description:
              'Prepare the data room for controlled investor access and ongoing due diligence.'
          }
        ]}
      />

      <RelatedServices
        links={[
          {
            label: 'Fundraising Assistance',
            href: '/services/fundraising-assistance'
          },
          {
            label: 'Investor Discovery',
            href: '/services/investor-discovery'
          },
          {
            label: 'Investor Outreach',
            href: '/services/investor-outreach'
          },
          {
            label: 'Financial Modeling',
            href: '/services/financial-modeling'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is a startup data room?',
            answer:
              'A data room is an organized collection of company documents that can be shared with investors during fundraising and due diligence.'
          },
          {
            question: 'What documents should be included?',
            answer:
              'Documents can include financial, legal, corporate, commercial, customer, team, product, technology and fundraising information depending on the company and investor requirements.'
          },
          {
            question: 'When should a startup create a data room?',
            answer:
              'Founders can prepare a data room before active investor outreach so that important information is organized when investor requests begin.'
          },
          {
            question: 'Can you organize an existing data room?',
            answer:
              'Yes. Existing documentation can be reviewed and reorganized into a clearer structure based on the company and fundraising requirements.'
          },
          {
            question: 'Why is a structured data room important?',
            answer:
              'A structured data room can make it easier for founders and investors to locate relevant information during the due diligence process.'
          },
          {
            question: 'Does a data room guarantee investment?',
            answer:
              'No. A data room is one part of the fundraising process and does not guarantee an investment or investor decision.'
          }
        ]}
      />

      <FinalCTA
        title="Prepare Your Investor Data Room"
        description="Organize your company information before investor due diligence begins."
      />

    </div>
  );
};


// ============================================================
// PITCH DECK
// ============================================================


export const PitchDeck = () => {

  usePageSEO(
    'Startup Pitch Deck Creation | Investor Pitch Deck | FounderScale Capital',
    'Create an investor-ready startup pitch deck with FounderScale Capital. Structure your story, market opportunity, business model, traction, team and financial information for fundraising.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={BarChart3}
        title="Pitch Deck Creation"
        subtitle="Tell your startup story clearly and effectively to investors"
        description="FounderScale Capital helps startups structure investor-facing pitch decks around the problem, solution, market opportunity, business model, traction, team, financial requirements and fundraising objective."
        features={[
          'Narrative structure development',
          'Problem-solution articulation',
          'Market opportunity presentation',
          'Business model explanation',
          'Traction and metrics presentation',
          'Competitive landscape',
          'Team presentation',
          'Financial projections',
          'Fundraising requirement',
          'Investor Q&A appendix'
        ]}
        gradient="from-purple-400 to-violet-500"
      />

      <ContentSection
        title="Build a Clear Investor Narrative"
        description="A pitch deck should help investors understand what the company does, why the opportunity matters and how the business can grow."
        items={[
          {
            title: 'Problem & Solution',
            description:
              'Clearly explain the customer problem, why it matters and how the startup addresses it.'
          },
          {
            title: 'Market Opportunity',
            description:
              'Present the relevant market, customer segments, opportunity and growth drivers in a structured way.'
          },
          {
            title: 'Business Model & Traction',
            description:
              'Explain how the business generates revenue and present relevant traction, customers, growth or operating metrics.'
          },
          {
            title: 'Team & Fundraising',
            description:
              'Present the founding team, relevant capabilities, fundraising requirement and intended use of capital.'
          }
        ]}
      />

      <ProcessSection
        title="Pitch Deck Creation Process"
        description="The deck is developed around the startup's actual business, stage and fundraising objective."
        steps={[
          {
            title: 'Understand',
            description:
              'Understand the startup, market, business model, traction and fundraising objective.'
          },
          {
            title: 'Structure',
            description:
              'Develop a logical investor narrative and organize the key information.'
          },
          {
            title: 'Build',
            description:
              'Develop the slide content, supporting visuals and financial information.'
          },
          {
            title: 'Refine',
            description:
              'Review the deck for clarity, consistency and investor-facing communication.'
          }
        ]}
      />

      <RelatedServices
        links={[
          {
            label: 'Fundraising Assistance',
            href: '/services/fundraising-assistance'
          },
          {
            label: 'Investor Discovery',
            href: '/services/investor-discovery'
          },
          {
            label: 'Investor Outreach',
            href: '/services/investor-outreach'
          },
          {
            label: 'Financial Modeling',
            href: '/services/financial-modeling'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is included in a startup pitch deck?',
            answer:
              'A pitch deck commonly presents the problem, solution, market opportunity, business model, traction, competition, team, financial information and fundraising requirement.'
          },
          {
            question: 'Can you improve an existing pitch deck?',
            answer:
              'Yes. An existing deck can be reviewed and restructured around a clearer investor narrative and the startup’s current fundraising requirements.'
          },
          {
            question: 'Should the pitch deck include financial projections?',
            answer:
              'Financial information can be included where relevant, particularly when presenting the company’s growth plans and fundraising requirements.'
          },
          {
            question: 'Do you create different versions of a pitch deck?',
            answer:
              'Depending on the fundraising requirement, founders may use different versions or lengths of investor materials for different stages of the fundraising process.'
          },
          {
            question: 'Can the deck be customized for investors?',
            answer:
              'Yes. The core deck can be structured around the startup while specific investor communication can highlight information relevant to an investor’s focus.'
          },
          {
            question: 'Does a pitch deck guarantee funding?',
            answer:
              'No. A pitch deck is a communication and fundraising document and does not guarantee investment or investor interest.'
          }
        ]}
      />

      <FinalCTA
        title="Build an Investor-Ready Pitch Deck"
        description="Tell us about your startup and fundraising objective to discuss how your investor presentation can be structured."
      />

    </div>
  );
};


// ============================================================
// FINANCIAL MODELING
// ============================================================


export const FinancialModeling = () => {

  usePageSEO(
    'Startup Financial Modeling Services | Financial Projections | FounderScale Capital',
    'Build an investor-ready startup financial model with FounderScale Capital, including revenue projections, operating expenses, unit economics, scenarios, burn rate, runway and valuation analysis.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Briefcase}
        title="Financial Model Creation"
        subtitle="Numbers that provide a structured view of your growth plan"
        description="FounderScale Capital helps startups develop financial models covering revenue, expenses, unit economics, cash requirements, scenarios, burn rate, runway and other metrics relevant to fundraising."
        features={[
          '3–5 year financial projections',
          'Revenue forecasting',
          'Unit economics analysis',
          'Customer acquisition cost modeling',
          'Lifetime value calculations',
          'Operating expense breakdown',
          'Burn rate and runway analysis',
          'Scenario analysis',
          'Key business metrics',
          'Valuation framework'
        ]}
        gradient="from-teal-400 to-emerald-500"
      />

      <ContentSection
        title="Build a Structured Startup Financial Model"
        description="A financial model helps founders explain how the business may develop financially and what capital may be required to execute the growth plan."
        items={[
          {
            title: 'Revenue Forecasting',
            description:
              'Model revenue streams, customer assumptions, pricing, growth rates and other relevant revenue drivers.'
          },
          {
            title: 'Operating Expenses',
            description:
              'Organize expected operating expenses and major cost categories to understand the company’s projected cash requirements.'
          },
          {
            title: 'Unit Economics',
            description:
              'Analyze relevant metrics such as customer acquisition cost, lifetime value, gross margin and other business-specific operating metrics.'
          },
          {
            title: 'Scenario Planning',
            description:
              'Use base, optimistic and conservative scenarios to understand how changes in assumptions can affect the financial outlook.'
          }
        ]}
      />

      <ProcessSection
        title="Financial Modeling Process"
        description="The model is structured around the company's business model, operating assumptions and fundraising requirements."
        steps={[
          {
            title: 'Understand',
            description:
              'Review the business model, revenue streams, costs, customers and current financial information.'
          },
          {
            title: 'Model',
            description:
              'Build revenue, expense, cash flow and operating assumptions into the model.'
          },
          {
            title: 'Test',
            description:
              'Review scenarios and key assumptions to understand how changes affect projected results.'
          },
          {
            title: 'Present',
            description:
              'Organize the model so relevant financial information can be communicated during fundraising.'
          }
        ]}
      />

      <RelatedServices
        links={[
          {
            label: 'Fundraising Assistance',
            href: '/services/fundraising-assistance'
          },
          {
            label: 'Pitch Deck Creation',
            href: '/services/pitch-deck'
          },
          {
            label: 'Investor Discovery',
            href: '/services/investor-discovery'
          },
          {
            label: 'Data Room Creation',
            href: '/services/data-room'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is startup financial modeling?',
            answer:
              'Startup financial modeling involves creating structured projections around revenue, expenses, cash requirements, unit economics and other financial assumptions relevant to the business.'
          },
          {
            question: 'How many years should a startup financial model cover?',
            answer:
              'The appropriate period depends on the startup and fundraising requirement. The service can include multi-year projections based on the company’s stage and planning needs.'
          },
          {
            question: 'Do you include unit economics?',
            answer:
              'Yes. Where relevant, the model can include metrics such as customer acquisition cost, lifetime value, margins and other business-specific unit economics.'
          },
          {
            question: 'Can the model include multiple scenarios?',
            answer:
              'Yes. Scenario analysis can be used to examine how different assumptions affect projected financial performance.'
          },
          {
            question: 'Can financial models support fundraising?',
            answer:
              'A structured financial model can help founders explain financial assumptions, growth plans and capital requirements during investor conversations.'
          },
          {
            question: 'Does a financial model guarantee funding?',
            answer:
              'No. Financial modeling is a fundraising preparation tool and does not guarantee investment.'
          }
        ]}
      />

      <FinalCTA
        title="Build a Fundraising-Ready Financial Model"
        description="Share your business model, current financial information and fundraising objective to discuss your financial modeling requirements."
      />

    </div>
  );
};


// ============================================================
// INVESTOR SERVICES
// ============================================================


export const StartupScouting = () => {

  usePageSEO(
    'Startup Scouting Services for Investors | FounderScale Capital',
    'FounderScale Capital provides startup scouting and deal-flow support for investors through curated startup pipelines, sector research, opportunity analysis and investment-thesis alignment.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Search}
        title="Startup Scouting"
        subtitle="Find and evaluate investment opportunities across markets"
        description="FounderScale Capital provides curated startup scouting and deal-flow support for investors looking for opportunities aligned with their investment thesis, sector preferences and stage focus."
        features={[
          'Curated startup pipeline',
          'Sector-specific deal flow',
          'Opportunity research',
          'Founder background research',
          'Market opportunity analysis',
          'Competitive landscape mapping',
          'Financial health assessment',
          'Investment thesis alignment'
        ]}
        gradient="from-indigo-400 to-blue-500"
      />

      <ContentSection
        title="Structured Startup Deal Flow"
        description="Startup scouting helps investors organize potential opportunities according to their investment requirements."
        items={[
          {
            title: 'Investment Thesis Alignment',
            description:
              'Review potential opportunities against the investor’s stated sector, stage, geography and investment preferences.'
          },
          {
            title: 'Market Opportunity',
            description:
              'Research the startup’s market, business model, customer opportunity and competitive environment.'
          },
          {
            title: 'Founder & Company Research',
            description:
              'Organize available information about the founding team, company and business development.'
          },
          {
            title: 'Opportunity Pipeline',
            description:
              'Create a structured pipeline of startups for further review, conversations and investment evaluation.'
          }
        ]}
      />

      <ProcessSection
        title="Startup Scouting Process"
        steps={[
          {
            title: 'Define',
            description:
              'Understand the investor’s thesis, stage, sector and geographic requirements.'
          },
          {
            title: 'Source',
            description:
              'Research and identify startups that may fit the defined criteria.'
          },
          {
            title: 'Evaluate',
            description:
              'Organize available information about the company, market, team and opportunity.'
          },
          {
            title: 'Present',
            description:
              'Present relevant opportunities for further investor review and discussion.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is startup scouting?',
            answer:
              'Startup scouting is the process of identifying and organizing potential startup investment opportunities for investors based on defined criteria.'
          },
          {
            question: 'Can startups be filtered by sector?',
            answer:
              'Yes. Startup scouting can be organized around sector and industry preferences.'
          },
          {
            question: 'Can opportunities be filtered by stage?',
            answer:
              'Yes. Stage can be one of the criteria used to identify relevant startups.'
          },
          {
            question: 'Do you provide deal flow?',
            answer:
              'The service is designed to support curated startup opportunity research and pipeline development for investors.'
          }
        ]}
      />

      <FinalCTA
        title="Looking for Relevant Startup Opportunities?"
        description="Share your investment thesis, sector focus and stage preferences to discuss startup scouting requirements."
      />

    </div>
  );
};


export const StartupMatchmaking = () => {

  usePageSEO(
    'Startup Investor Matchmaking | FounderScale Capital',
    'FounderScale Capital helps connect startups and investors based on investment thesis, sector, stage, geography and other relevant investment criteria.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Target}
        title="Startup Matchmaking"
        subtitle="Connect investors and startups around relevant investment criteria"
        description="FounderScale Capital supports investor-startup matching using factors such as investment thesis, sector preference, stage focus, geography and potential cheque size."
        features={[
          'Investment thesis profiling',
          'Sector and stage filtering',
          'Geographic preference matching',
          'Cheque-size alignment',
          'Portfolio considerations',
          'Co-investor opportunities',
          'Lead and follow-on preferences',
          'Connection facilitation'
        ]}
        gradient="from-pink-400 to-rose-500"
      />

      <ContentSection
        title="Investor-Startup Matching Based on Relevant Criteria"
        description="A structured matching process can help investors and startups identify potential areas of alignment before beginning a conversation."
        items={[
          {
            title: 'Investment Thesis',
            description:
              'Consider whether the startup’s business and fundraising requirement align with the investor’s stated investment interests.'
          },
          {
            title: 'Sector & Stage',
            description:
              'Compare the startup’s sector and funding stage with the investor’s stated focus.'
          },
          {
            title: 'Geography',
            description:
              'Consider geographic and market preferences where relevant to the investment mandate.'
          },
          {
            title: 'Investment Requirements',
            description:
              'Review potential cheque size, lead or follow-on preferences and other relevant investment requirements.'
          }
        ]}
      />

      <ProcessSection
        title="Matching Process"
        steps={[
          {
            title: 'Profile',
            description:
              'Understand the investor and startup requirements.'
          },
          {
            title: 'Compare',
            description:
              'Compare sector, stage, geography and investment preferences.'
          },
          {
            title: 'Identify',
            description:
              'Identify potential areas of alignment.'
          },
          {
            title: 'Connect',
            description:
              'Facilitate appropriate introductions or next-step conversations where applicable.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is startup-investor matchmaking?',
            answer:
              'It is the process of identifying potential alignment between startups and investors based on investment criteria such as sector, stage, geography and investment thesis.'
          },
          {
            question: 'What factors are considered?',
            answer:
              'Factors may include sector, investment stage, geography, investment thesis, portfolio focus and potential cheque size.'
          },
          {
            question: 'Does matchmaking guarantee an investment?',
            answer:
              'No. Matching identifies potential relevance but does not guarantee investor interest or investment.'
          }
        ]}
      />

      <FinalCTA
        title="Looking for Strategic Investor-Startup Connections?"
        description="Share your investment requirements or fundraising profile to discuss potential matchmaking opportunities."
      />

    </div>
  );
};


export const InvestorFundraising = () => {

  usePageSEO(
    'Fundraising Support for Investors & Portfolio Companies | FounderScale Capital',
    'FounderScale Capital supports investors and portfolio companies with fundraising strategy, next-round preparation, investor matching and follow-on fundraising support.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={DollarSign}
        title="Fundraising Assistance for Investors"
        subtitle="Support your portfolio companies through their next fundraising round"
        description="FounderScale Capital supports investors, funds and portfolio companies with fundraising preparation, investor discovery, co-investor introductions and follow-on round strategy."
        features={[
          'Portfolio company strategy sessions',
          'Next-round preparation',
          'Co-investor introductions',
          'Investor discovery',
          'Strategic investor matching',
          'Bridge round support',
          'Fundraising documentation support',
          'Exit preparation support'
        ]}
        gradient="from-emerald-400 to-green-500"
      />

      <ContentSection
        title="Support Portfolio Companies Through Fundraising"
        description="Existing investors may need additional support when portfolio companies prepare for follow-on capital."
        items={[
          {
            title: 'Next-Round Preparation',
            description:
              'Help portfolio companies structure fundraising materials, financial information and investor positioning for the next round.'
          },
          {
            title: 'Co-Investor Discovery',
            description:
              'Identify potential investors whose investment focus may complement the company and existing investor group.'
          },
          {
            title: 'Strategic Investor Matching',
            description:
              'Research investors that may bring relevant capital, industry knowledge, distribution or strategic relationships.'
          },
          {
            title: 'Fundraising Execution',
            description:
              'Support portfolio companies through investor outreach, meetings, diligence and follow-on fundraising activities.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'Who can use this service?',
            answer:
              'The service is designed for investors, funds, family offices and other investment organizations supporting portfolio companies.'
          },
          {
            question: 'Can you help with follow-on fundraising?',
            answer:
              'Yes. Support can include preparation, investor discovery, co-investor research and fundraising execution.'
          },
          {
            question: 'Can you identify co-investors?',
            answer:
              'Investor research can be structured around potential co-investors based on sector, stage, geography and investment requirements.'
          }
        ]}
      />

      <FinalCTA
        title="Support Your Portfolio Companies"
        description="Tell us about your portfolio company and upcoming capital requirement to discuss fundraising support."
      />

    </div>
  );
};


export const StrategicAcquisition = () => {

  usePageSEO(
    'Strategic Acquisition & M&A Advisory | FounderScale Capital',
    'FounderScale Capital supports strategic acquisition and M&A processes through target identification, valuation analysis, due diligence, deal structuring, negotiation guidance and buyer or seller introductions.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Handshake}
        title="Strategic Acquisition"
        subtitle="Identify and evaluate strategic acquisition and exit opportunities"
        description="FounderScale Capital provides support around strategic acquisitions and exits, including target identification, valuation analysis, due diligence support, deal structuring, negotiation guidance and buyer or seller introductions."
        features={[
          'Target identification',
          'Valuation analysis',
          'Due diligence support',
          'Deal structuring',
          'Negotiation guidance',
          'Integration planning',
          'Exit strategy advisory',
          'Buyer and seller introductions'
        ]}
        gradient="from-amber-400 to-yellow-500"
      />

      <ContentSection
        title="Structured Strategic Acquisition Support"
        description="Acquisition processes require careful research, evaluation and coordination across multiple stages."
        items={[
          {
            title: 'Target Identification',
            description:
              'Research potential acquisition targets based on strategic objectives, sector, market and business requirements.'
          },
          {
            title: 'Valuation & Analysis',
            description:
              'Organize relevant company and market information to support valuation and acquisition discussions.'
          },
          {
            title: 'Due Diligence',
            description:
              'Support the organization and review of relevant commercial, financial and business information during diligence.'
          },
          {
            title: 'Deal Execution',
            description:
              'Support deal structuring, negotiation preparation, introductions and other activities through the transaction process.'
          }
        ]}
      />

      <ProcessSection
        title="Strategic Acquisition Process"
        steps={[
          {
            title: 'Define',
            description:
              'Understand the acquisition or exit objective and relevant criteria.'
          },
          {
            title: 'Identify',
            description:
              'Research potential targets, buyers or strategic counterparties.'
          },
          {
            title: 'Evaluate',
            description:
              'Review business, market, financial and strategic information.'
          },
          {
            title: 'Execute',
            description:
              'Support transaction discussions, diligence and deal execution.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is strategic acquisition support?',
            answer:
              'It is support for businesses or investors evaluating acquisitions, strategic transactions or exits, including target research, analysis, diligence and transaction preparation.'
          },
          {
            question: 'Can you help identify acquisition targets?',
            answer:
              'Yes. Target research can be structured around the buyer’s strategic, sector, geographic and business requirements.'
          },
          {
            question: 'Do you provide valuation analysis?',
            answer:
              'Valuation analysis can form part of the acquisition preparation process depending on the transaction requirements.'
          },
          {
            question: 'Can you support due diligence?',
            answer:
              'Yes. Support can include organizing and reviewing relevant information as part of the diligence process.'
          }
        ]}
      />

      <FinalCTA
        title="Exploring an Acquisition or Strategic Exit?"
        description="Tell us about your acquisition or transaction objectives to discuss the relevant support."
      />

    </div>
  );
};


// ============================================================
// STAGE PAGES
// ============================================================


export const EarlyStage = () => {

  usePageSEO(
    'Early Stage Startup Funding | Pre-Seed & Seed Fundraising | FounderScale Capital',
    'Fundraising support for early-stage startups at ideation, pre-seed and seed stages, including investor preparation, investor discovery, pitch development and outreach.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={TrendingUp}
        title="Early Stage Funding"
        subtitle="Support for founders at ideation, pre-seed and seed stages"
        description="FounderScale Capital helps early-stage founders prepare for fundraising and connect with relevant angels, micro-VCs and seed investors."
        features={[
          'Idea validation guidance',
          'Pre-seed strategy',
          'Seed round preparation',
          'Investor readiness',
          'Angel investor research',
          'Micro-VC discovery',
          'MVP positioning',
          'Early traction storytelling'
        ]}
        gradient="from-green-400 to-emerald-500"
      />

      <ContentSection
        title="Fundraising Support for Early-Stage Startups"
        description="Early-stage fundraising often requires founders to clearly communicate the problem, solution, market opportunity, business model and early evidence of demand."
        items={[
          {
            title: 'Investor Readiness',
            description:
              'Prepare the startup and fundraising materials for investor conversations.'
          },
          {
            title: 'Pre-Seed Fundraising',
            description:
              'Structure the fundraising story and identify potential investors relevant to an early-stage capital requirement.'
          },
          {
            title: 'Seed Fundraising',
            description:
              'Support startups preparing to raise seed capital from angels, micro-VCs and seed-stage funds.'
          },
          {
            title: 'Early Traction Storytelling',
            description:
              'Present available traction, customers, product progress and market validation clearly to investors.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'Which startups are considered early stage?',
            answer:
              'Early-stage startups can include companies at ideation, pre-seed and seed stages, depending on their development and fundraising position.'
          },
          {
            question: 'Do you support pre-seed fundraising?',
            answer:
              'Yes. Support can include investor readiness, fundraising strategy, investor discovery, pitch preparation and outreach.'
          },
          {
            question: 'Do you work with seed-stage startups?',
            answer:
              'Yes. Seed-stage fundraising can include investor preparation, investor targeting and structured outreach.'
          }
        ]}
      />

      <FinalCTA
        title="Preparing for Your First Fundraise?"
        description="Tell us about your startup, current traction and capital requirement to discuss early-stage fundraising support."
      />

    </div>
  );
};


export const GrowthStage = () => {

  usePageSEO(
    'Growth Stage Startup Funding | Series A & B Fundraising | FounderScale Capital',
    'Growth-stage fundraising support for startups preparing for Series A and Series B rounds, including investor targeting, growth metrics, financial modeling and fundraising strategy.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Building2}
        title="Growth Stage Funding"
        subtitle="Support for startups preparing for Series A and Series B fundraising"
        description="FounderScale Capital helps growth-stage companies structure their fundraising story, prepare investor materials and identify relevant institutional investors."
        features={[
          'Series A preparation',
          'Series B strategy',
          'Institutional investor research',
          'Growth metrics presentation',
          'Unit economics review',
          'Scaling strategy presentation',
          'Lead investor identification',
          'Syndicate building'
        ]}
        gradient="from-blue-400 to-indigo-500"
      />

      <ContentSection
        title="Fundraising Support for Growth-Stage Companies"
        description="Growth-stage investors often evaluate traction, growth, market size, unit economics, scalability and the company's ability to deploy additional capital."
        items={[
          {
            title: 'Growth Metrics',
            description:
              'Structure important growth, revenue, customer and operating metrics for investor communication.'
          },
          {
            title: 'Unit Economics',
            description:
              'Present relevant acquisition, retention, margin and customer economics where applicable.'
          },
          {
            title: 'Scaling Strategy',
            description:
              'Explain how the company plans to deploy additional capital to expand the business.'
          },
          {
            title: 'Institutional Investor Targeting',
            description:
              'Identify investors whose stage, sector and investment focus may align with the company.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is growth-stage fundraising?',
            answer:
              'Growth-stage fundraising generally refers to capital raised by companies with an established product, customers or traction that are seeking additional capital to scale.'
          },
          {
            question: 'Do you support Series A fundraising?',
            answer:
              'Yes. Support can include investor preparation, investor discovery, pitch development, financial modeling and outreach.'
          },
          {
            question: 'Do you support Series B fundraising?',
            answer:
              'Yes. Growth-stage fundraising support can be structured around the company’s stage, metrics, capital requirement and investor profile.'
          }
        ]}
      />

      <FinalCTA
        title="Preparing for Your Next Growth Round?"
        description="Tell us about your traction, current stage and fundraising requirement to discuss growth-stage support."
      />

    </div>
  );
};


export const LateStage = () => {

  usePageSEO(
    'Late Stage Funding | Series C+ & Pre-IPO Fundraising | FounderScale Capital',
    'Late-stage fundraising support for established companies preparing for Series C+, growth equity, strategic investment or pre-IPO capital.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Crown}
        title="Late Stage Funding"
        subtitle="Series C+ and pre-IPO fundraising support for established companies"
        description="FounderScale Capital supports established companies preparing for significant capital raises, strategic investment, growth equity opportunities or pre-IPO fundraising."
        features={[
          'Series C+ fundraising preparation',
          'Pre-IPO funding strategy',
          'Growth equity investor research',
          'Strategic investor matching',
          'IPO preparation guidance',
          'Secondary market support',
          'Late-stage VC connections',
          'Crossover investor research'
        ]}
        gradient="from-purple-400 to-violet-500"
      />

      <ContentSection
        title="Support for Later-Stage Capital Requirements"
        description="Later-stage companies may have more complex capital requirements and a broader range of potential institutional and strategic investors."
        items={[
          {
            title: 'Growth Equity',
            description:
              'Research and target growth equity investors relevant to the company’s stage and capital requirement.'
          },
          {
            title: 'Strategic Investment',
            description:
              'Identify strategic investors where industry relationships, distribution or commercial partnerships may be relevant.'
          },
          {
            title: 'Pre-IPO Preparation',
            description:
              'Support fundraising preparation for companies moving toward larger institutional capital events.'
          },
          {
            title: 'Late-Stage Investor Research',
            description:
              'Map relevant late-stage venture, crossover and other institutional investors.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is late-stage fundraising?',
            answer:
              'Late-stage fundraising refers to capital raising by more established companies, including Series C and later rounds, growth equity and certain pre-IPO transactions.'
          },
          {
            question: 'Do you support Series C+ fundraising?',
            answer:
              'Yes. Support can include investor discovery, fundraising preparation and strategic investor research.'
          },
          {
            question: 'Do you work with strategic investors?',
            answer:
              'Strategic investor research can be part of a later-stage fundraising strategy where relevant.'
          }
        ]}
      />

      <FinalCTA
        title="Planning a Major Capital Raise?"
        description="Tell us about your company, current stage and capital requirement to discuss late-stage fundraising support."
      />

    </div>
  );
};


// ============================================================
// INVESTOR TYPE PAGES
// ============================================================


export const HNIAngel = () => {

  usePageSEO(
    'HNI & Angel Investors for Startups | FounderScale Capital',
    'Connect with relevant HNI and angel investors through FounderScale Capital for early-stage fundraising opportunities.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Users}
        title="HNI & Angel Investors"
        subtitle="Connect with high-net-worth individuals and angel investors"
        description="FounderScale Capital helps startups research and connect with relevant HNI and angel investor opportunities, particularly for early-stage fundraising requirements."
        features={[
          'HNI investor research',
          'Angel investor discovery',
          'Investor introductions',
          'Syndicate opportunities',
          'Stage-aligned investor research',
          'Sector-aligned matching'
        ]}
        gradient="from-amber-400 to-orange-500"
      />

      <ContentSection
        title="Angel & HNI Investor Research"
        description="Angel investors and HNIs can be relevant sources of capital for early-stage startups. Investor research can be organized around stage, sector, geography and investment preference."
        items={[
          {
            title: 'Early-Stage Focus',
            description:
              'Identify investors who may participate in early-stage opportunities.'
          },
          {
            title: 'Sector Alignment',
            description:
              'Research investor interests and previous investment activity by sector.'
          },
          {
            title: 'Cheque Size',
            description:
              'Consider available information about potential investment size when building an investor pipeline.'
          },
          {
            title: 'Relationship Building',
            description:
              'Support professional introductions and structured investor communication where appropriate.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What are HNI investors?',
            answer:
              'HNI refers to high-net-worth individuals who may invest their personal capital into businesses or investment opportunities.'
          },
          {
            question: 'What are angel investors?',
            answer:
              'Angel investors are individuals who invest capital into startups, often at early stages.'
          },
          {
            question: 'Can startups be matched with angel investors?',
            answer:
              'Investor research can identify potential angel investors based on stage, sector and other relevant criteria.'
          }
        ]}
      />

      <FinalCTA
        title="Looking for Angel or HNI Investors?"
        description="Share your startup stage, sector and fundraising requirement to discuss relevant investor research."
      />

    </div>
  );
};


export const MicroVCs = () => {

  usePageSEO(
    'Micro VC & Venture Capital Investors | FounderScale Capital',
    'Find relevant micro-VC and venture capital investors based on startup stage, sector, geography, investment thesis and fundraising requirements.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={TrendingUp}
        title="Micro VCs & VCs"
        subtitle="Research relevant micro-VCs and venture capital firms"
        description="FounderScale Capital helps startups identify venture capital firms whose investment stage, sector, geography and thesis may align with their fundraising requirements."
        features={[
          'Micro-VC research',
          'Traditional VC research',
          'Sector-specialized investor matching',
          'Stage-aligned investor research',
          'Lead investor research',
          'Co-investor opportunities'
        ]}
        gradient="from-blue-400 to-indigo-500"
      />

      <ContentSection
        title="Venture Capital Investor Research"
        description="VC research can help founders understand which firms may be relevant before beginning outreach."
        items={[
          {
            title: 'Investment Stage',
            description:
              'Research funds according to their preferred startup stages and round sizes.'
          },
          {
            title: 'Sector Focus',
            description:
              'Review sector and industry preferences across venture capital firms.'
          },
          {
            title: 'Investment Thesis',
            description:
              'Analyze publicly available investment theses and portfolio companies.'
          },
          {
            title: 'Lead & Follow-On',
            description:
              'Consider whether an investor may participate as a lead, co-investor or follow-on investor where information is available.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is a micro-VC?',
            answer:
              'A micro-VC is generally a smaller venture capital fund that invests in startups, often at earlier stages than larger institutional funds.'
          },
          {
            question: 'Can you identify sector-specific VCs?',
            answer:
              'Yes. Investor research can be organized around sector and industry preferences.'
          },
          {
            question: 'Can VC investors be filtered by stage?',
            answer:
              'Yes. Stage preference can be used as a criterion when organizing potential investors.'
          }
        ]}
      />

      <FinalCTA
        title="Looking for Relevant VC Investors?"
        description="Share your startup stage, sector and fundraising requirement to discuss venture capital investor research."
      />

    </div>
  );
};


export const FamilyOffices = () => {

  usePageSEO(
    'Family Office Investors for Startups | FounderScale Capital',
    'Connect startups with relevant family office investment opportunities through structured investor research based on sector, stage, geography and investment preferences.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Building}
        title="Family Offices"
        subtitle="Connect with family offices for direct investment opportunities"
        description="FounderScale Capital helps startups research relevant single-family and multi-family offices based on their investment preferences and the startup’s fundraising requirements."
        features={[
          'Single-family office research',
          'Multi-family office research',
          'Direct investment opportunities',
          'Long-term capital research',
          'Sector-aligned investor mapping',
          'Cross-border investor research'
        ]}
        gradient="from-emerald-400 to-green-500"
      />

      <ContentSection
        title="Family Office Investor Research"
        description="Family offices can have different investment mandates, time horizons and sector preferences. Structured research helps founders understand potential alignment."
        items={[
          {
            title: 'Investment Mandate',
            description:
              'Research publicly available information about the family office’s investment interests and approach.'
          },
          {
            title: 'Direct Investments',
            description:
              'Identify family offices that participate in direct investments where relevant information is available.'
          },
          {
            title: 'Sector Preferences',
            description:
              'Organize potential investors according to sector and industry focus.'
          },
          {
            title: 'Geographic Focus',
            description:
              'Consider geographic and market preferences where relevant to the fundraising requirement.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is a family office?',
            answer:
              'A family office is an organization established to manage wealth, investments and related financial affairs for a family or group of families.'
          },
          {
            question: 'Do family offices invest in startups?',
            answer:
              'Some family offices participate in private and startup investments. Their preferences vary by organization.'
          },
          {
            question: 'Can you research family offices by sector?',
            answer:
              'Yes. Sector and investment preferences can be included in investor research.'
          }
        ]}
      />

      <FinalCTA
        title="Looking for Family Office Investors?"
        description="Tell us about your startup and capital requirement to discuss relevant family office investor research."
      />

    </div>
  );
};


export const PrivateEquity = () => {

  usePageSEO(
    'Private Equity Investors for Growth Companies | FounderScale Capital',
    'Research private equity and growth equity investors for established companies seeking growth capital, strategic investment or buyout opportunities.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Briefcase}
        title="Private Equity"
        subtitle="Growth equity and buyout opportunities"
        description="FounderScale Capital helps established companies research private equity and growth equity investors based on industry, stage, transaction requirements and strategic objectives."
        features={[
          'Growth equity research',
          'Buyout investor research',
          'PE-backed opportunities',
          'Operational expertise research',
          'Industry-focused PE firms',
          'International PE research'
        ]}
        gradient="from-purple-400 to-violet-500"
      />

      <ContentSection
        title="Private Equity Investor Research"
        description="Private equity investors can have specific sector, transaction size, ownership and growth requirements."
        items={[
          {
            title: 'Growth Equity',
            description:
              'Research investors focused on providing growth capital to established businesses.'
          },
          {
            title: 'Buyout Opportunities',
            description:
              'Identify private equity firms whose investment strategies may include acquisition or buyout transactions.'
          },
          {
            title: 'Industry Expertise',
            description:
              'Consider sector and industry expertise when researching potential PE investors.'
          },
          {
            title: 'Strategic Fit',
            description:
              'Review potential alignment between the company’s objectives and investor investment mandate.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What is private equity?',
            answer:
              'Private equity refers to investment in privately held companies, commonly through buyout, growth equity or other structured transactions.'
          },
          {
            question: 'When should a company approach private equity?',
            answer:
              'Private equity may be relevant for more established businesses depending on their scale, profitability, growth requirements and transaction objectives.'
          },
          {
            question: 'Can you identify industry-focused PE firms?',
            answer:
              'Yes. Investor research can be organized around industry and investment focus.'
          }
        ]}
      />

      <FinalCTA
        title="Exploring Private Equity Capital?"
        description="Tell us about your company, growth objectives and capital requirement to discuss relevant PE investor research."
      />

    </div>
  );
};


export const CorporateInvestors = () => {

  usePageSEO(
    'Corporate Investors & Strategic Investment | FounderScale Capital',
    'Connect startups with relevant corporate investors and strategic investment opportunities based on sector, technology, geography and business objectives.'
  );

  return (
    <div className="min-h-screen">

      <ServicePageTemplate
        icon={Building2}
        title="Corporate Investors"
        subtitle="Strategic corporate venture capital and partnership opportunities"
        description="FounderScale Capital helps startups research relevant corporate investors and strategic partners based on industry, technology, geography and business objectives."
        features={[
          'Corporate VC research',
          'Strategic investor discovery',
          'Industry partnerships',
          'Distribution opportunities',
          'Technology partnerships',
          'Strategic relationship research'
        ]}
        gradient="from-cyan-400 to-blue-500"
      />

      <ContentSection
        title="Strategic Corporate Investor Research"
        description="Corporate investors can potentially bring capital as well as commercial relationships, industry expertise, technology and distribution opportunities."
        items={[
          {
            title: 'Corporate Venture Capital',
            description:
              'Research corporate venture arms and strategic investors whose investment focus may align with the startup.'
          },
          {
            title: 'Strategic Partnerships',
            description:
              'Identify organizations where a commercial or strategic relationship may be relevant alongside investment.'
          },
          {
            title: 'Distribution Opportunities',
            description:
              'Consider potential distribution, customer access or market expansion opportunities where relevant.'
          },
          {
            title: 'Technology Partnerships',
            description:
              'Research potential technology, product or ecosystem partnerships that may support the startup’s objectives.'
          }
        ]}
      />

      <FAQSection
        faqs={[
          {
            question: 'What are corporate investors?',
            answer:
              'Corporate investors are companies or corporate venture arms that invest in or partner with startups and other businesses.'
          },
          {
            question: 'Why might a startup approach a corporate investor?',
            answer:
              'Depending on the investor, a corporate relationship may provide capital as well as strategic, commercial, technology or distribution opportunities.'
          },
          {
            question: 'Can corporate investors be researched by industry?',
            answer:
              'Yes. Industry and strategic relevance can be considered when identifying potential corporate investors.'
          }
        ]}
      />

      <FinalCTA
        title="Looking for Strategic Corporate Investors?"
        description="Tell us about your startup, industry and strategic objectives to discuss relevant corporate investor research."
      />

    </div>
  );
};
